// ─── Plan & Entitlement Domain Layer ───────────────────────────────
// CANONICAL SOURCE OF TRUTH:
// The `subscriptions` table (written by Paddle webhooks) is the single
// source of truth for the user's effective plan. The `plan` column in
// that table maps directly to PlanCode.
//
// BILLING PROVIDER: Paddle (only). No Stripe integration exists.
// ────────────────────────────────────────────────────────────────────

export type PlanCode = "demo" | "free" | "pro" | "elite";

export const PLAN_CODES: PlanCode[] = ["demo", "free", "pro", "elite"];

// ─── Feature Keys ──────────────────────────────────────────────────

export const FEATURES = {
  ACCOUNTS_MAX: "accounts.max",
  DASHBOARD_ADVANCED: "dashboard.advanced",
  DASHBOARD_PREMIUM_WIDGETS: "dashboard.premium_widgets",
  JOURNAL_TRADE_LIMIT: "journal.trade_limit",
  ANALYTICS_ADVANCED: "analytics.advanced",
  ANALYTICS_PREMIUM_DIAGNOSTICS: "analytics.premium_diagnostics",
  FRAMEWORK_MAX_STRATEGIES: "framework.max_strategies",
  FRAMEWORK_MAX_BEHAVIORS: "framework.max_behaviors",
  FRAMEWORK_MAX_RISK_PROFILES: "framework.max_risk_profiles",
  FRAMEWORK_MAX_EXECUTION_PLANS: "framework.max_execution_plans",
  FRAMEWORK_MAX_BLUEPRINTS: "framework.max_blueprints",
  CALENDAR_BASIC: "calendar.basic",
  CALENDAR_ADVANCED: "calendar.advanced",
  CALENDAR_PREMIUM: "calendar.premium",
  ASSISTANT_READ_ONLY: "assistant.read_only",
  ASSISTANT_INTERACTIVE: "assistant.interactive",
  ASSISTANT_ADVANCED: "assistant.advanced",
  AI_MONTHLY_CREDITS: "ai.monthly_credits",
  REPORTS_STANDARD: "reports.standard",
  REPORTS_PREMIUM: "reports.premium",
  PORTFOLIO_ADVANCED: "portfolio.advanced",
  SCORE_STANDARD: "score.standard",
  SCORE_ADVANCED: "score.advanced",
  ALERTS_ADVANCED: "alerts.advanced",
} as const;

export type FeatureKey = (typeof FEATURES)[keyof typeof FEATURES];

// ─── Entitlement Types ─────────────────────────────────────────────

export interface Entitlement {
  feature_key: string;
  value_type: "boolean" | "number" | "text";
  value_boolean: boolean | null;
  value_number: number | null;
  value_text: string | null;
}

export type EntitlementMap = Record<string, Entitlement>;

/**
 * Represents a row from the canonical `subscriptions` table (Paddle).
 * This is the ONLY subscription shape the app uses.
 */
export interface PaddleSubscription {
  id: string;
  user_id: string;
  paddle_subscription_id: string | null;
  paddle_customer_id: string | null;
  product_id: string | null;
  price_id: string | null;
  plan: string; // maps to PlanCode
  status: string;
  billing_cycle: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean | null;
  environment: string;
  created_at: string;
  updated_at: string;
}

export interface UserUsageMonthly {
  ai_credits_used: number;
  exports_used: number;
  assistant_actions_used: number;
  month_key: string;
}

export interface Plan {
  code: PlanCode;
  name: string;
  description: string | null;
  sort_order: number;
  is_active: boolean;
  monthly_price: number;
  yearly_price: number;
}

// ─── Effective Plan Resolution ────────────────────────────────────
// Priority:
//   1. active/trialing paid subscription → paid plan
//   2. canceled but current_period_end in the future → still paid
//   3. expired/inactive/missing → free
// ──────────────────────────────────────────────────────────────────

const ACTIVE_STATUSES = ["active", "trialing"];

export function resolveEffectivePlan(sub: PaddleSubscription | null): PlanCode {
  if (!sub) return "free";

  const plan = (sub.plan as PlanCode) || "free";
  if (!PLAN_CODES.includes(plan)) return "free";

  // Demo plan: check expiration
  if (plan === "demo") {
    const expiresAt = (sub as any).demo_expires_at;
    if (expiresAt && new Date(expiresAt) < new Date()) return "free";
    return "demo";
  }

  // Active or trialing → paid plan
  if (ACTIVE_STATUSES.includes(sub.status)) return plan;

  // Canceled but still in period → paid until period end
  if (sub.status === "canceled" && sub.current_period_end) {
    const endDate = new Date(sub.current_period_end);
    if (endDate > new Date()) return plan;
  }

  return "free";
}

// ─── Entitlement Utilities ─────────────────────────────────────────

export function hasFeature(entitlements: EntitlementMap, featureKey: string): boolean {
  const e = entitlements[featureKey];
  if (!e) return false;
  if (e.value_type === "boolean") return e.value_boolean === true;
  if (e.value_type === "number") return (e.value_number ?? 0) > 0;
  return !!e.value_text;
}

export function getNumericLimit(
  entitlements: EntitlementMap,
  featureKey: string,
  fallback: number = 0
): number {
  const e = entitlements[featureKey];
  if (!e || e.value_type !== "number") return fallback;
  return e.value_number ?? fallback;
}

export function isLimitReached(
  entitlements: EntitlementMap,
  featureKey: string,
  currentCount: number
): boolean {
  const limit = getNumericLimit(entitlements, featureKey, 0);
  return currentCount >= limit;
}

export function isUpgradeRequired(entitlements: EntitlementMap, featureKey: string): boolean {
  return !hasFeature(entitlements, featureKey);
}

export function getRemainingAiCredits(
  entitlements: EntitlementMap,
  usage: UserUsageMonthly | null
): number {
  const limit = getNumericLimit(entitlements, FEATURES.AI_MONTHLY_CREDITS, 0);
  const used = usage?.ai_credits_used ?? 0;
  return Math.max(0, limit - used);
}

export function getUpgradePlan(currentPlan: PlanCode): PlanCode | null {
  if (currentPlan === "demo") return "free";
  if (currentPlan === "free") return "pro";
  if (currentPlan === "pro") return "elite";
  return null;
}

export const PLAN_DISPLAY: Record<PlanCode, { name: string; tagline: string; color: string }> = {
  demo: { name: "Demo", tagline: "30-day full access trial", color: "text-blue-500" },
  free: { name: "Free", tagline: "Taste the workflow", color: "text-muted-foreground" },
  pro: { name: "Pro", tagline: "Full serious trading system", color: "text-primary" },
  elite: { name: "Elite", tagline: "Your performance edge", color: "text-amber-500" },
};

// ─── Default Entitlements (Free plan fallback) ─────────────────────

export const FREE_ENTITLEMENTS: EntitlementMap = {
  [FEATURES.ACCOUNTS_MAX]: { feature_key: FEATURES.ACCOUNTS_MAX, value_type: "number", value_boolean: null, value_number: 1, value_text: null },
  [FEATURES.DASHBOARD_ADVANCED]: { feature_key: FEATURES.DASHBOARD_ADVANCED, value_type: "boolean", value_boolean: false, value_number: null, value_text: null },
  [FEATURES.DASHBOARD_PREMIUM_WIDGETS]: { feature_key: FEATURES.DASHBOARD_PREMIUM_WIDGETS, value_type: "boolean", value_boolean: false, value_number: null, value_text: null },
  [FEATURES.JOURNAL_TRADE_LIMIT]: { feature_key: FEATURES.JOURNAL_TRADE_LIMIT, value_type: "number", value_boolean: null, value_number: 20, value_text: null },
  [FEATURES.ANALYTICS_ADVANCED]: { feature_key: FEATURES.ANALYTICS_ADVANCED, value_type: "boolean", value_boolean: false, value_number: null, value_text: null },
  [FEATURES.ANALYTICS_PREMIUM_DIAGNOSTICS]: { feature_key: FEATURES.ANALYTICS_PREMIUM_DIAGNOSTICS, value_type: "boolean", value_boolean: false, value_number: null, value_text: null },
  [FEATURES.FRAMEWORK_MAX_STRATEGIES]: { feature_key: FEATURES.FRAMEWORK_MAX_STRATEGIES, value_type: "number", value_boolean: null, value_number: 1, value_text: null },
  [FEATURES.FRAMEWORK_MAX_BEHAVIORS]: { feature_key: FEATURES.FRAMEWORK_MAX_BEHAVIORS, value_type: "number", value_boolean: null, value_number: 1, value_text: null },
  [FEATURES.FRAMEWORK_MAX_RISK_PROFILES]: { feature_key: FEATURES.FRAMEWORK_MAX_RISK_PROFILES, value_type: "number", value_boolean: null, value_number: 1, value_text: null },
  [FEATURES.FRAMEWORK_MAX_EXECUTION_PLANS]: { feature_key: FEATURES.FRAMEWORK_MAX_EXECUTION_PLANS, value_type: "number", value_boolean: null, value_number: 1, value_text: null },
  [FEATURES.FRAMEWORK_MAX_BLUEPRINTS]: { feature_key: FEATURES.FRAMEWORK_MAX_BLUEPRINTS, value_type: "number", value_boolean: null, value_number: 1, value_text: null },
  [FEATURES.CALENDAR_BASIC]: { feature_key: FEATURES.CALENDAR_BASIC, value_type: "boolean", value_boolean: true, value_number: null, value_text: null },
  [FEATURES.CALENDAR_ADVANCED]: { feature_key: FEATURES.CALENDAR_ADVANCED, value_type: "boolean", value_boolean: false, value_number: null, value_text: null },
  [FEATURES.CALENDAR_PREMIUM]: { feature_key: FEATURES.CALENDAR_PREMIUM, value_type: "boolean", value_boolean: false, value_number: null, value_text: null },
  [FEATURES.ASSISTANT_READ_ONLY]: { feature_key: FEATURES.ASSISTANT_READ_ONLY, value_type: "boolean", value_boolean: true, value_number: null, value_text: null },
  [FEATURES.ASSISTANT_INTERACTIVE]: { feature_key: FEATURES.ASSISTANT_INTERACTIVE, value_type: "boolean", value_boolean: false, value_number: null, value_text: null },
  [FEATURES.ASSISTANT_ADVANCED]: { feature_key: FEATURES.ASSISTANT_ADVANCED, value_type: "boolean", value_boolean: false, value_number: null, value_text: null },
  [FEATURES.AI_MONTHLY_CREDITS]: { feature_key: FEATURES.AI_MONTHLY_CREDITS, value_type: "number", value_boolean: null, value_number: 0, value_text: null },
  [FEATURES.REPORTS_STANDARD]: { feature_key: FEATURES.REPORTS_STANDARD, value_type: "boolean", value_boolean: false, value_number: null, value_text: null },
  [FEATURES.REPORTS_PREMIUM]: { feature_key: FEATURES.REPORTS_PREMIUM, value_type: "boolean", value_boolean: false, value_number: null, value_text: null },
  [FEATURES.PORTFOLIO_ADVANCED]: { feature_key: FEATURES.PORTFOLIO_ADVANCED, value_type: "boolean", value_boolean: false, value_number: null, value_text: null },
  [FEATURES.SCORE_STANDARD]: { feature_key: FEATURES.SCORE_STANDARD, value_type: "boolean", value_boolean: false, value_number: null, value_text: null },
  [FEATURES.SCORE_ADVANCED]: { feature_key: FEATURES.SCORE_ADVANCED, value_type: "boolean", value_boolean: false, value_number: null, value_text: null },
  [FEATURES.ALERTS_ADVANCED]: { feature_key: FEATURES.ALERTS_ADVANCED, value_type: "boolean", value_boolean: false, value_number: null, value_text: null },
};

// ─── Paddle Price IDs (human-readable external_id values) ─────────
// These are placeholder IDs that must match products in Paddle catalog.
// Actual commercial pricing is configured in Paddle, not hardcoded here.
export const PADDLE_PRICE_IDS = {
  pro_monthly: "pro_monthly",
  pro_yearly: "pro_yearly",
  elite_monthly: "elite_monthly",
  elite_yearly: "elite_yearly",
} as const;
